import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { AvailableModules, Features } from 'generated/featureDefinitions'

import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import subscriptions from 'modules/billing/store/collections/subscriptions'
import { useItemProvider } from 'modules/processes/components/ItemWizard/Providers/item'
import type { SelectorProps } from 'modules/processes/lib/types'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

import ItemCard from '../../components/ItemCard'

export const Selector = ({ title, description, identifier, onSelect }: SelectorProps) => {
  const { itemType } = useItemProvider()
  const [openUpgradeModal, setOpenUpgradeModal] = useState<boolean>(false)
  const [alreadySuccess, setAlreadySuccess] = useState<boolean>(false)
  const subscription = subscriptions.current
  const { category } = useParams()

  const { featureAvailability, loading } = useFeatureAvailability(
    Features.processes_custom_email_action
  )

  const handleOpenUpgradeModal = () => {
    setOpenUpgradeModal(true)
    track(trackingEvents.processes.actionUpsellClicked, {
      category,
      identifier,
    })
  }

  const onClose = () => {
    if (alreadySuccess) {
      setOpenUpgradeModal(false)
      return
    }

    track(trackingEvents.processes.actionUpsellClosed, {
      category,
      identifier,
    })
    setOpenUpgradeModal(false)
  }

  const onSuccess = () => {
    track(trackingEvents.processes.actionUpsellSuccess, {
      category,
      identifier,
    })
    setAlreadySuccess(true)
    setOpenUpgradeModal(false)
  }

  if (loading) return null

  return (
    <>
      <ItemCard
        title={title}
        description={description}
        onSelect={() => onSelect(identifier)}
        itemType={identifier}
        value={itemType}
        isUpgradeSelector={!featureAvailability.available}
        openUpgradeModal={handleOpenUpgradeModal}
      />
      {openUpgradeModal && (
        <RequestUpgradeModal
          requestReason={
            subscription.isLegacy ? ValidReasons.MissingFeatureLegacy : ValidReasons.MissingFeature
          }
          featureRequested={Features.processes_custom_email_action}
          requestSource="Workflow send custom email action configuration"
          title={i18n.t('processes.process_settings.upgrade_modal.automatic_email.title')}
          description={i18n.t(
            'processes.process_settings.upgrade_modal.automatic_email.description'
          )}
          moduleRequested={AvailableModules.processes}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </>
  )
}
